// Notification type + destination options, named after the app's current
// structure (one design since 2026-10-26; ADMIN_PANEL_MAP.md M8/M9, F3).
// Kept free of React so it is unit-testable.

import { CHAINS } from "../TokenListings/constants";

// `type` values are a contract with the API and the app's FCM topics
// (BiorLabs-<TYPE>); the labels are the app's own Settings switches, so the
// sender picks the switch users see.
export const NOTIFICATION_TYPES = [
  { value: "GENERAL", label: "General", hint: "No switch in the app: everyone with notifications on gets it." },
  { value: "NEW_FEATURE", label: "New features", hint: "Settings switch “New features”." },
  { value: "NEW_TOKEN_LISTING", label: "New token listings", hint: "Settings switch “New token listings”." },
  { value: "NEW_BLOG_ARTICLE", label: "New blog article", hint: "Settings switch “New blog article”." },
  { value: "PROMOTION", label: "Promotions", hint: "Settings switch “Promotions”." },
  { value: "APP_UPDATE", label: "App updates", hint: "Settings switch “App updates”." },
];

export const typeLabel = (v) => (NOTIFICATION_TYPES.find((t) => t.value === v) || {}).label || v || "General";

// Where a tap opens. Only screens the current app has. The server builds the
// link from the key (free text never becomes a link).
export const DESTINATIONS = [
  { key: "notifications", label: "Notifications (default)" },
  { key: "market_tokens", label: "Market › Tokens" },
  { key: "market_xstocks", label: "Market › xStocks" },
  { key: "market_memefi", label: "Market › MemeFi" },
  { key: "token", label: "A token's page" },
  { key: "rewards", label: "Rewards" },
  { key: "explore", label: "Explore" },
  { key: "fiat", label: "Fiat" },
  { key: "settings", label: "Settings" },
  { key: "url", label: "A web page (https)" },
];

export const TOKEN_CHAINS = CHAINS;

export const emptyDestination = () => ({ key: "notifications", network_id: "", contract: "", url: "" });

export function destinationLabel(d) {
  if (!d || !d.key || d.key === "notifications") return "Notifications";
  const base = (DESTINATIONS.find((x) => x.key === d.key) || {}).label || d.key;
  if (d.key === "token") {
    const chain = CHAINS.find((c) => String(c.network_id) === String(d.network_id));
    return `${base}: ${chain ? chain.name : `chain ${d.network_id}`} ${d.contract || ""}`.trim();
  }
  if (d.key === "url") return `${base}: ${d.url || ""}`.trim();
  return base;
}

// Client-side check; the API validates again. Returns an error string or "".
export function destinationError(d) {
  if (!d || !d.key || d.key === "notifications") return "";
  if (!DESTINATIONS.some((x) => x.key === d.key)) return "Choose where the notification opens.";
  if (d.key === "token") {
    if (String(d.network_id || "") === "") return "Choose the token's chain.";
    if (!String(d.contract || "").trim()) return "Enter the token's contract address, or native.";
  }
  if (d.key === "url" && !/^https:\/\/[^\s/]+\.[^\s]+/.test(String(d.url || "").trim())) {
    return "The web page must be a full https:// address.";
  }
  return "";
}

// The body field the API reads (`destination`).
export function destinationPayload(d) {
  if (!d || !d.key || d.key === "notifications") return { key: "notifications" };
  if (d.key === "token") return { key: "token", network_id: Number(d.network_id), contract: String(d.contract || "").trim() };
  if (d.key === "url") return { key: "url", url: String(d.url || "").trim() };
  return { key: d.key };
}

// A row's `destination` from the API -> form state.
export function destinationFromRow(n) {
  const d = (n && n.destination) || null;
  if (!d) return emptyDestination();
  return { ...emptyDestination(), ...d, network_id: d.network_id == null ? "" : String(d.network_id) };
}
